import type { ImageSourcePropType } from 'react-native'
import { v4 as uuidv4 } from 'uuid'

import { SearchGroupNameEnumv2 } from 'api/gen'
import { getSearchPropConfig } from 'features/navigation/navigators/SearchStackNavigator/getSearchPropConfig'
import { useSearch } from 'features/search/context/SearchWrapper'
import { isOnlyOnline } from 'features/search/helpers/categoriesHelpers/categoriesHelpers'
import { getCategoryLabelParts } from 'features/search/helpers/getCategoryLabelParts/getCategoryLabelParts'
import { useAvailableCategories } from 'features/search/helpers/useAvailableCategories/useAvailableCategories'
import { useHasAThematicPageList } from 'features/search/helpers/useHasAThematicPageList/useHasAThematicPageList'
import { useSearchGroupLabelMapping } from 'libs/subcategories/mappings'
import { useSubcategoriesQuery } from 'queries/subcategories/useSubcategoriesQuery'
import { CategoryButtonProps } from 'shared/categoryButton/CategoryButton'
import { BackgroundColorKey, BorderColorKey, IllustrationColorKey } from 'theme/types'

export type MappingOutput = Pick<
  CategoryButtonProps,
  'label' | 'navigateTo' | 'onBeforeNavigate' | 'height' | 'style'
> & {
  searchLandingPosition: number | undefined
  fillColor: BackgroundColorKey | IllustrationColorKey
  borderColor: BorderColorKey
  imageSource?: ImageSourcePropType
  labelParts?: readonly string[]
}

export type ListCategoryButtonProps = MappingOutput[]

export function categoriesSortPredicate(a: MappingOutput, b: MappingOutput): number {
  const positionA: number = a?.searchLandingPosition || 0
  const positionB: number = b?.searchLandingPosition || 0
  return positionA - positionB
}

export const useSortedSearchCategories = (): ListCategoryButtonProps => {
  const searchGroupLabelMapping = useSearchGroupLabelMapping()
  const categories = useAvailableCategories()
  const { data } = useSubcategoriesQuery()
  const hasAThematicSearch = useHasAThematicPageList()
  const { searchState, dispatch } = useSearch()
  const searchId = uuidv4()
  const navigateTo = (facetFilter: SearchGroupNameEnumv2) => {
    const searchTabConfig = getSearchPropConfig(
      hasAThematicSearch.includes(facetFilter) ? 'ThematicSearch' : 'SearchResults',
      {
        offerCategories: [facetFilter],
        isFullyDigitalOffersCategory: data && isOnlyOnline(data, facetFilter),
        searchId,
      }
    )
    return searchTabConfig
  }

  const onBeforeNavigate = (facetFilter: SearchGroupNameEnumv2) => {
    dispatch({
      type: 'SET_STATE',
      payload: { ...searchState, offerCategories: [facetFilter], searchId },
    }) // we'd rather have it in url params but URL is not the only source of truth. When it is, this dispatch should be removed.
  }
  return categories
    .map<MappingOutput>((category) => {
      const label = searchGroupLabelMapping?.[category.facetFilter] || ''

      return {
        label,
        navigateTo: navigateTo(category.facetFilter),
        onBeforeNavigate: () => onBeforeNavigate(category.facetFilter),
        searchLandingPosition: category.searchLandingPosition,
        borderColor: category.borderColor,
        fillColor: category.fillColor,
        imageSource: category.illustration,
        labelParts: getCategoryLabelParts(label),
      }
    })
    .sort(categoriesSortPredicate)
}
